import {
  TGetCountCaseMatchesSummaryResDto,
  TGetCountSummaryReqDto,
  TGetCountSummaryResDto,
  TExportBulkCsvPayload,
} from "@/api/services/main/dashboard/dtos/get-count-summary.dto";
import {
  TPerformanceDetailBaseReqDto,
  TPerformanceDetailWithStatusReqDto,
  TGetSummaryResDto,
  TGetMatchingStatisticsResDto,
  TGetStaffPerformanceResDto,
  TGetIndustryPerformanceResDto,
  TGetPartnerPerformanceResDto,
  TGetClientTypeOfferResDto,
  TGetClientTypeNewJobResDto,
  TGetClientTypeStatisticsResDto,
} from "@/api/services/main/dashboard/dtos/performance-detail.dto";
import {
  TGetMonthlyTargetReqDto,
  TGetMonthlyTargetResDto,
  TUpsertMonthlyTargetReqDto,
  TUpsertMonthlyTargetResDto,
} from "@/api/services/main/dashboard/dtos/monthly-target.dto";
import {
  TRecentOfferAcceptedResDto,
  TRecentOfferAcceptedPaginatedResDto,
  TRecentOfferAcceptedClassificationBulkResDto,
} from "@/api/services/main/dashboard/dtos/recent-offer-accepted.dto";
import { TMatchingResultsReqDto, TMatchingResultsResDto } from "@/api/services/main/dashboard/dtos/matching-results.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { getFilenameFromContentDisposition } from "@/utils/export";
import { getPerformanceDetailPeriod } from "@/features/dashboard/utils/get-performance-detail-period";
import { useQuery } from "@tanstack/react-query";

/**
 * Fetch dashboard count summary API
 */
export const fetchCountSummaryApi = async (request?: TGetCountSummaryReqDto): Promise<TGetCountSummaryResDto> => {
  const url = createUrlWithParams("/dashboard/count-summary", request);
  const { data } = await clientApi.get<TGetCountSummaryResDto>(url);
  return data;
};

/**
 * Hook to fetch dashboard count summary
 */
export const useCountSummary = (request?: TGetCountSummaryReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_COUNT_SUMMARY, request],
    queryFn: () => fetchCountSummaryApi(request),
  });
};

/**
 * Fetch dashboard count case matches summary API
 */
export const fetchCountCaseMatchesSummaryApi = async (request?: TGetCountSummaryReqDto): Promise<TGetCountCaseMatchesSummaryResDto> => {
  const url = createUrlWithParams("/dashboard/count-case-matches-summary", request);
  const { data } = await clientApi.get<TGetCountCaseMatchesSummaryResDto>(url);
  return data;
};

/**
 * Hook to fetch dashboard count case matches summary
 */
export const useCountCaseMatchesSummary = (request?: TGetCountSummaryReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_COUNT_CASE_MATCHES_SUMMARY, request],
    queryFn: () => fetchCountCaseMatchesSummaryApi(request),
  });
};

/**
 * Fetch monthly target API
 */
export const fetchMonthlyTargetApi = async (request: TGetMonthlyTargetReqDto): Promise<TGetMonthlyTargetResDto> => {
  const url = createUrlWithParams("/dashboard/monthly-targets", request);
  const { data } = await clientApi.get<TGetMonthlyTargetResDto>(url);
  return data;
};

/**
 * Hook to fetch monthly target
 */
export const useMonthlyTarget = (request: TGetMonthlyTargetReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_MONTHLY_TARGET, request],
    queryFn: () => fetchMonthlyTargetApi(request),
  });
};

/**
 * Create or update monthly target API
 */
export const upsertMonthlyTargetApi = async (request: TUpsertMonthlyTargetReqDto): Promise<TUpsertMonthlyTargetResDto> => {
  const { data } = await clientApi.post<TUpsertMonthlyTargetResDto>("/dashboard/monthly-targets/upsert", request);
  return data;
};

/**
 * Fetch recent offer-accepted case matches
 */
export const fetchRecentOfferAcceptedApi = async (limit?: number): Promise<TRecentOfferAcceptedResDto> => {
  const url = createUrlWithParams("/dashboard/recent-offer-accepted", limit ? { limit: String(limit) } : undefined);
  const { data } = await clientApi.get<TRecentOfferAcceptedResDto>(url);
  return data;
};

/**
 * Hook to fetch recent offer-accepted case matches
 */
export const useRecentOfferAccepted = (limit?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_RECENT_OFFER_ACCEPTED, limit],
    queryFn: () => fetchRecentOfferAcceptedApi(limit),
  });
};

/**
 * Fetch paginated offer-accepted case matches (for modal)
 */
export const fetchRecentOfferAcceptedPaginatedApi = async (page: number, perPage: number): Promise<TRecentOfferAcceptedPaginatedResDto> => {
  const url = createUrlWithParams("/dashboard/recent-offer-accepted", {
    page: String(page),
    perPage: String(perPage),
  });
  const { data } = await clientApi.get<TRecentOfferAcceptedPaginatedResDto>(url);
  return data;
};

/**
 * Hook to fetch paginated offer-accepted case matches (for modal)
 */
export const useRecentOfferAcceptedPaginated = (page: number, perPage: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_RECENT_OFFER_ACCEPTED_PAGINATED, page, perPage],
    queryFn: () => fetchRecentOfferAcceptedPaginatedApi(page, perPage),
    enabled,
  });
};

/**
 * Fetch lean classification data (for mismatch validation) of a bulk case-match selection —
 * isAll or explicit ids, mirroring the export-bulk endpoints. Backend batches internally
 * (skip/take, no one-to-many joins) so this stays safe at large totalCount, unlike calling
 * fetchRecentOfferAcceptedPaginatedApi(1, totalCount) which fans out under join and has no cap.
 */
export const fetchRecentOfferAcceptedClassificationBulkApi = async (
  payload: TExportBulkCsvPayload,
): Promise<TRecentOfferAcceptedClassificationBulkResDto> => {
  const { data } = await clientApi.post<TRecentOfferAcceptedClassificationBulkResDto>(
    "/dashboard/case-matches/recent-offer-accepted/classification",
    payload,
  );
  return data;
};

/**
 * Performance detail summary API
 */
export const fetchPerformanceDetailSummaryApi = async (request?: TPerformanceDetailBaseReqDto): Promise<TGetSummaryResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/summary", request);
  const { data } = await clientApi.get<TGetSummaryResDto>(url);
  return data;
};

export const usePerformanceDetailSummary = (request?: TPerformanceDetailBaseReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_SUMMARY, request],
    queryFn: () => fetchPerformanceDetailSummaryApi(request),
  });
};

/**
 * Performance detail matching statistics API
 */
export const fetchPerformanceDetailMatchingApi = async (request?: TPerformanceDetailBaseReqDto): Promise<TGetMatchingStatisticsResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/matching-statistics", request);
  const { data } = await clientApi.get<TGetMatchingStatisticsResDto>(url);
  return data;
};

export const usePerformanceDetailMatching = (request?: TPerformanceDetailBaseReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_MATCHING, request],
    queryFn: () => fetchPerformanceDetailMatchingApi(request),
  });
};

/**
 * Performance detail staff performance API
 */
export const fetchPerformanceDetailStaffApi = async (request?: TPerformanceDetailWithStatusReqDto): Promise<TGetStaffPerformanceResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/staff-performance", request);
  const { data } = await clientApi.get<TGetStaffPerformanceResDto>(url);
  return data;
};

export const usePerformanceDetailStaff = (request?: TPerformanceDetailWithStatusReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_STAFF, request],
    queryFn: () => fetchPerformanceDetailStaffApi(request),
  });
};

/**
 * Performance detail industry performance API
 */
export const fetchPerformanceDetailIndustryApi = async (request?: TPerformanceDetailWithStatusReqDto): Promise<TGetIndustryPerformanceResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/industry-performance", request);
  const { data } = await clientApi.get<TGetIndustryPerformanceResDto>(url);
  return data;
};

export const usePerformanceDetailIndustry = (request?: TPerformanceDetailWithStatusReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_INDUSTRY, request],
    queryFn: () => fetchPerformanceDetailIndustryApi(request),
  });
};

/**
 * Performance detail partner performance API
 */
export const fetchPerformanceDetailPartnerApi = async (request?: TPerformanceDetailWithStatusReqDto): Promise<TGetPartnerPerformanceResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/partner-performance", request);
  const { data } = await clientApi.get<TGetPartnerPerformanceResDto>(url);
  return data;
};

export const usePerformanceDetailPartner = (request?: TPerformanceDetailWithStatusReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_PARTNER, request],
    queryFn: () => fetchPerformanceDetailPartnerApi(request),
  });
};

/**
 * Performance detail 顧客区分別内定数 API
 */
export const fetchPerformanceDetailClientTypeOfferApi = async (request?: TPerformanceDetailBaseReqDto): Promise<TGetClientTypeOfferResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/client-type-offer-statistics", request);
  const { data } = await clientApi.get<TGetClientTypeOfferResDto>(url);
  return data;
};

export const usePerformanceDetailClientTypeOffer = (request?: TPerformanceDetailBaseReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_CLIENT_TYPE_OFFER, request],
    queryFn: () => fetchPerformanceDetailClientTypeOfferApi(request),
  });
};

/**
 * Performance detail 顧客区分別新規求人数 API
 */
export const fetchPerformanceDetailClientTypeNewJobApi = async (request?: TPerformanceDetailBaseReqDto): Promise<TGetClientTypeNewJobResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/client-type-new-job-statistics", request);
  const { data } = await clientApi.get<TGetClientTypeNewJobResDto>(url);
  return data;
};

export const usePerformanceDetailClientTypeNewJob = (request?: TPerformanceDetailBaseReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_CLIENT_TYPE_NEW_JOB, request],
    queryFn: () => fetchPerformanceDetailClientTypeNewJobApi(request),
  });
};

/**
 * Performance detail 区分別年間実績表 API
 */
export const fetchPerformanceDetailClientTypeApi = async (request?: TPerformanceDetailWithStatusReqDto): Promise<TGetClientTypeStatisticsResDto> => {
  const url = createUrlWithParams("/dashboard/performance-detail/client-type-statistics", request);
  const { data } = await clientApi.get<TGetClientTypeStatisticsResDto>(url);
  return data;
};

export const usePerformanceDetailClientType = (request?: TPerformanceDetailWithStatusReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_PERFORMANCE_DETAIL_CLIENT_TYPE, request],
    queryFn: () => fetchPerformanceDetailClientTypeApi(request),
  });
};

/**
 * Export performance detail CSV
 */
export const exportPerformanceDetailCsvApi = async (targetYear: string, targetMonth: string, showConversionRate: boolean) => {
  const response = await clientApi.get(`/dashboard/performance-detail/export-csv`, {
    params: { targetYear, targetMonth, showConversionRate: String(showConversionRate) },
    responseType: "blob",
  });

  // Content-Disposition is not exposed via CORS, so build the filename client-side.
  // startYear = fiscal start year, matching the BE-generated title 年間実績表（${startYear}年）.
  const { startYear } = getPerformanceDetailPeriod(Number(targetYear), Number(targetMonth));
  const fileName = `年間実績表（${startYear}年）.csv`;

  const blob = new Blob([response.data], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Update case match status from dashboard
 */
export const updateDashboardCaseMatchStatusApi = async (caseMatchId: number, payload: { status: number; talentStatus?: number }) => {
  const { data } = await clientApi.patch(`/dashboard/case-matches/${caseMatchId}/status`, payload);
  return data;
};

/**
 * Export company CSV for a case match
 */
export const exportCompanyCsvToVisaApi = async (caseMatchId: number, companyName?: string) => {
  const response = await clientApi.get(`/dashboard/case-matches/${caseMatchId}/export-company-csv-to-visa`, {
    responseType: "blob",
  });

  const fileName = getFilenameFromContentDisposition(response.headers, `${companyName ?? "company"}_${Date.now()}.csv`);

  // Trigger download
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Export talent CSV for a case match
 */
export const exportTalentCsvToVisaApi = async (caseMatchId: number, talentName?: string) => {
  const response = await clientApi.get(`/dashboard/case-matches/${caseMatchId}/export-talent-csv-to-visa`, {
    responseType: "blob",
  });

  const fileName = getFilenameFromContentDisposition(response.headers, `${talentName}_${Date.now()}.csv`);

  // Trigger download
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Export talent CSVs (zip) for multiple case matches
 */
export const exportTalentCsvToVisaBulkApi = async (payload: TExportBulkCsvPayload): Promise<{ blob: Blob; fileName: string }> => {
  const response = await clientApi.post("/dashboard/case-matches/export-talent-csv-to-visa/bulk", payload, { responseType: "blob" });

  const fallback = `人材CSV_${Date.now()}.zip`;
  const fileName = getFilenameFromContentDisposition(response.headers, fallback);

  return { blob: response.data, fileName };
};

/**
 * Export company CSVs (zip) for multiple case matches, deduped by company
 */
export const exportCompanyCsvToVisaBulkApi = async (payload: TExportBulkCsvPayload): Promise<{ blob: Blob; fileName: string }> => {
  const response = await clientApi.post("/dashboard/case-matches/export-company-csv-to-visa/bulk", payload, { responseType: "blob" });

  const fallback = `企業CSV_${Date.now()}.zip`;
  const fileName = getFilenameFromContentDisposition(response.headers, fallback);

  return { blob: response.data, fileName };
};

/**
 * Fetch paginated matching-results (CaseMatch rows) for a given metric + targetMonth.
 * Powers MatchingResultsModal on dashboard.
 */
export const fetchMatchingResultsApi = async (params: TMatchingResultsReqDto): Promise<TMatchingResultsResDto> => {
  // Only stringify defined params; createUrlWithParams omits undefined keys.
  const query: Record<string, string> = {
    metric: params.metric,
    targetMonth: params.targetMonth,
  };
  if (params.page !== undefined) query.page = String(params.page);
  if (params.perPage !== undefined) query.perPage = String(params.perPage);
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortDir) query.sortDir = params.sortDir;

  const url = createUrlWithParams("/dashboard/case-matches", query);
  const { data } = await clientApi.get<TMatchingResultsResDto>(url);
  return data;
};

/**
 * Hook for paginated matching-results. Pass `enabled=false` when modal is closed to skip fetch.
 */
export const useMatchingResultsPaginated = (params: TMatchingResultsReqDto, enabled: boolean = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD.FETCH_MATCHING_RESULTS_PAGINATED, params],
    queryFn: () => fetchMatchingResultsApi(params),
    enabled,
  });
};
